import type { JSX } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useT } from '@/i18n';
import './IntroSplash.css';

interface IntroSplashProps {
  onDismiss: () => void;
}

interface Particle {
  id: number;
  left: number;
  top: number;
  size: number;
  duration: number;
  delay: number;
}

function buildParticles(): Particle[] {
  return Array.from({ length: 32 }, (_, i) => ({
    id: i,
    left:     Math.random() * 100,
    top:      Math.random() * 100,
    size:     1.5 + Math.random() * 3,
    duration: 2.5 + Math.random() * 3.5,
    delay:    Math.random() * 4,
  }));
}

const ARC_INDICES = [1, 2, 3, 4] as const;

export default function IntroSplash({ onDismiss }: IntroSplashProps): JSX.Element {
  const t = useT();
  const [closing, setClosing] = useState(false);
  const [particles] = useState<Particle[]>(buildParticles);
  const dismissedRef = useRef(false);

  function dismiss(): void {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    setClosing(true);
    setTimeout(onDismiss, 780);
  }

  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dismiss();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`IntroSplash${closing ? ' IntroSplash--closing' : ''}`}
      onClick={dismiss}
      aria-label={`${t.intro.press} ${t.intro.enter}`}
      role="button"
      tabIndex={0}
    >
      <div className="IntroSplash__nebula" />

      <div className="IntroSplash__particles" aria-hidden="true">
        {particles.map(p => (
          <span
            key={p.id}
            className="IntroSplash__particle"
            style={{
              left:              `${String(p.left)}%`,
              top:               `${String(p.top)}%`,
              width:             `${String(p.size)}px`,
              height:            `${String(p.size)}px`,
              animationDuration: `${String(p.duration)}s`,
              animationDelay:    `${String(p.delay)}s`,
            }}
          />
        ))}
      </div>

      <div className="IntroSplash__spinner" aria-hidden="true">
        <div className="IntroSplash__shock" />
        <div className="IntroSplash__shock IntroSplash__shock--b" />
        {ARC_INDICES.map(n => (
          <div key={n} className="IntroSplash__build">
            <div className={`IntroSplash__arc IntroSplash__arc--${String(n)}`} />
          </div>
        ))}
      </div>

      <div className="IntroSplash__logo">
        <div className="IntroSplash__logo-name">
          Pierre <span>Séré</span>
        </div>
        <div className="IntroSplash__logo-sub">{t.intro.role}</div>
      </div>

      <p className="IntroSplash__tagline">
        {t.intro.tagline}
      </p>

      <p className="IntroSplash__enter">
        {t.intro.press} <kbd>{t.intro.enter}</kbd>
      </p>
    </div>
  );
}
